import { displayOrDash } from '../data/recordRoom';
import type { EscalationTaskRow, EscalationUrgency } from '../data/escalationTasks';
import { formatDisplayDateTime } from '../utils/dateFormat';
import { uniqueColumnSuggestions } from '../utils/columnFilter';
import { caseApiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';
import { pickConnectedAgdFileRefAvailable } from './connectedAgdFileRefs';
import type { ApiResult } from './types';
import { adaptTaskAssignmentPage, type TaskParams } from './taskAssignment';

export type { TaskParams };

type ApiRow = Record<string, unknown>;

type ApiPage = {
  content?: ApiRow[];
  totalElements?: number;
  totalPages?: number;
};

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

function pickStr(obj: unknown, ...keys: string[]): string {
  const o = obj && typeof obj === 'object' ? (obj as Record<string, unknown>) : {};
  for (const k of keys) {
    const s = str(o[k]);
    if (s) return s;
  }
  return '';
}

function nestedId(obj: unknown): string {
  if (!obj || typeof obj !== 'object') return '';
  const o = obj as Record<string, unknown>;
  return str(o.id) || str(o.uuid);
}

function pickFileCategoryId(row: ApiRow): string {
  return (
    pickStr(
      row,
      'fileCategoryId',
      'file_category_id',
      'fileCategoryID',
      'file_category_uuid',
      'fileCategoryUuid',
      'file_categoryId',
      'file_categoryID',
      'categoryId',
      'category_id',
    ) || nestedId(row.fileCategory ?? row.file_category)
  );
}

function pickSubFileCategoryId(row: ApiRow): string {
  return (
    pickStr(
      row,
      'subFileCategoryId',
      'sub_file_category_id',
      'subFileCategoryID',
      'sub_file_category_uuid',
      'subFileCategoryUuid',
      'sub_category_id',
      'subCategoryId',
      'sub_category_uuid',
    ) || nestedId(row.subFileCategory ?? row.sub_file_category)
  );
}

const bool = (v: unknown) => (typeof v === 'boolean' ? v : undefined);

function normalizeUrgency(raw: string): EscalationUrgency {
  const x = raw.trim().toLowerCase();
  if (x === 'high' || x === 'h' || x === 'critical') return 'High';
  if (x === 'medium' || x === 'm' || x === 'normal') return 'Medium';
  if (x === 'low' || x === 'l') return 'Low';
  return 'Low';
}

function normalizeEscalationStatus(raw: string): EscalationTaskRow['status'] {
  const x = raw.trim().toLowerCase();
  if (x.includes('pending') && x.includes('assign')) return 'Pending Assignment';
  if (x === 'pending assignment') return 'Pending Assignment';
  if (x.includes('assign') && !x.includes('unassign')) return 'Assigned';
  return 'Pending Assignment';
}

/** DataTable column key → POST body filter field (camelCase, same family as CMS-CRC-17). */
export const ESCALATION_UNASSIGNED_FILTER_COL_MAP: Record<string, string> = {
  ref: 'fileRefNo',
  fileCategory: 'fileCategory',
  subFileCategory: 'subFileCategory',
  agency: 'submittedAgency',
  caseNumber: 'caseNumber',
  workflowStage: 'currentWorkflowStage',
  status: 'taskStatus',
  escalatedAt: 'escalatedAt',
  overdueTime: 'timeRemainingDisplay',
  urgency: 'urgencyLevel',
  currentAssignee: 'assignedUserName',
};

/** DataTable column key → sortBy value. */
export const ESCALATION_UNASSIGNED_SORT_COL_MAP: Record<string, string> = {
  ref: 'fileRefNo',
  fileCategory: 'fileCategory',
  subFileCategory: 'subFileCategory',
  agency: 'submittedAgency',
  caseNumber: 'caseNumber',
  workflowStage: 'currentWorkflowStage',
  status: 'taskStatus',
  escalatedAt: 'escalatedAt',
  overdueTime: 'timeRemainingDisplay',
  urgency: 'urgencyLevel',
  currentAssignee: 'assignedUserName',
};

/** Assigned tab — column key → POST body filter field (snake_case — matches API). */
export const ESCALATION_ASSIGNED_FILTER_COL_MAP: Record<string, string> = {
  ref: 'file_ref_no',
  fileCategory: 'file_category',
  subFileCategory: 'sub_file_category',
  agency: 'submitted_agency',
  caseNumber: 'case_number',
  workflowStage: 'current_workflow_stage',
  status: 'task_status',
  urgency: 'urgency_level',
  assignedOfficer: 'assigned_officer_name',
  supervisor: 'assigned_supervisor_name',
};

/** Assigned tab — column key → sort_by value (snake_case). */
export const ESCALATION_ASSIGNED_SORT_COL_MAP: Record<string, string> = {
  ref: 'file_ref_no',
  fileCategory: 'file_category',
  subFileCategory: 'sub_file_category',
  agency: 'submitted_agency',
  caseNumber: 'case_number',
  workflowStage: 'current_workflow_stage',
  status: 'task_status',
  urgency: 'urgency_level',
  assignedOfficer: 'assigned_officer_name',
  supervisor: 'assigned_supervisor_name',
};

/** POST `/escalation-tasks/unassign-task-case-requests` request body (mirrors `buildUnassignedListBody`). */
export function buildEscalationUnassignedListBody(params: TaskParams): Record<string, string> {
  const body: Record<string, string> = {
    sortBy: ESCALATION_UNASSIGNED_SORT_COL_MAP[params.sortBy ?? 'ref'] ?? 'fileRefNo',
    sortDirection: params.sortDir ?? 'ASC',
  };

  if (params.columnFilters) {
    for (const [colId, value] of Object.entries(params.columnFilters)) {
      const trimmed = value.trim();
      if (!trimmed) continue;
      const apiField = ESCALATION_UNASSIGNED_FILTER_COL_MAP[colId];
      if (apiField) body[apiField] = trimmed;
    }
  }

  return body;
}

/** POST `/escalation-tasks/assign-task-case-requests` request body (assigned escalation queue). */
export function buildEscalationAssignedListBody(params: TaskParams): Record<string, unknown> {
  const filters = params.columnFilters ?? {};
  const hasFilters = Object.values(filters).some((v) => v.trim());
  const sortKey = params.sortBy ?? 'ref';
  const sortDir = params.sortDir ?? 'ASC';

  // Swagger "Try it out" sends an empty body; camelCase sortBy caused null assignment fields from the API.
  if (!hasFilters && sortKey === 'ref' && sortDir === 'ASC') {
    return {};
  }

  const body: Record<string, unknown> = {
    sort_by: ESCALATION_ASSIGNED_SORT_COL_MAP[sortKey] ?? 'file_ref_no',
    sort_direction: sortDir,
  };

  for (const [colId, value] of Object.entries(filters)) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    const apiField = ESCALATION_ASSIGNED_FILTER_COL_MAP[colId];
    if (apiField) body[apiField] = trimmed;
  }

  return body;
}

/** Map CMS escalation list item → table row. */
export function mapEscalationApiRow(row: ApiRow): EscalationTaskRow {
  const ref = pickStr(row, 'fileRefNo', 'file_ref_no', 'file_ref', 'fileRef', 'ref');
  const id = pickStr(row, 'id', 'caseRequestId', 'case_request_id') || ref;
  const urgencyRaw = pickStr(row, 'urgencyLevel', 'urgency_level', 'urgency');
  const statusRaw = pickStr(row, 'taskStatus', 'task_status', 'status');
  const workflowStage = pickStr(
    row,
    'currentWorkflowStage',
    'current_workflow_stage',
    'workflowStage',
    'workflow_stage',
  );
  const overdueTime = pickStr(
    row,
    'timeRemainingDisplay',
    'time_remaining_display',
    'overdueTime',
    'overdue_time',
    'overdueTimeDisplay',
    'overdue_time_display',
  );
  const escalatedAtRaw = pickStr(row, 'escalatedAt', 'escalated_at');
  const escalatedAtDisplay = pickStr(row, 'escalatedAtDisplay', 'escalated_at_display');
  const escalatedAtLabel =
    escalatedAtDisplay ||
    (escalatedAtRaw ? formatDisplayDateTime(escalatedAtRaw) : '');
  const currentAssignee = pickStr(
    row,
    'assignedUserName',
    'assigned_user_name',
    'currentAssignee',
    'current_assignee',
    'assignedOfficerName',
    'assigned_officer_name',
    'assignedOfficer',
    'assigned_officer',
  );
  const assignedOfficerName = pickStr(
    row,
    'assignedOfficerName',
    'assigned_officer_name',
    'assignedOfficer',
    'assigned_officer',
  );
  const assignedSupervisorName = pickStr(
    row,
    'assignedSupervisorName',
    'assigned_supervisor_name',
    'supervisor',
    'assigned_supervisor',
    'supervisorName',
    'supervisor_name',
  );

  return {
    id,
    caseRequestId:
      pickStr(row, 'caseRequestId', 'case_request_id', 'caseRequestID', 'case_request_uuid') || id || undefined,
    fileCategoryId: pickFileCategoryId(row) || undefined,
    subFileCategoryId: pickSubFileCategoryId(row) || undefined,
    workflowInstanceStageUserTaskId: pickStr(
      row,
      'workflowInstanceStageUserTaskId',
      'workflow_instance_stage_user_task_id',
      'workflowInstanceStageUserTaskID',
      'workflow_instance_stage_user_task_uuid',
      'workflowInstanceStageUserTaskUuid',
      'workflow_user_task_id',
      'workflowUserTaskId',
      'userTaskId',
      'user_task_id',
      'taskId',
      'task_id',
    ) || undefined,
    isManualAllocation:
      bool(row['is_manual_allocation']) ??
      bool(row['isManualAllocation']),
    ref: displayOrDash(ref),
    fileCategory: displayOrDash(pickStr(row, 'fileCategory', 'file_category')),
    subFileCategory: displayOrDash(pickStr(row, 'subFileCategory', 'sub_file_category')),
    agency: displayOrDash(pickStr(row, 'submittedAgency', 'submitted_agency', 'agency')),
    isAgdSubmitted: bool(row['isAgdSubmitted']) ?? bool(row['is_agd_submitted']),
    caseNumber: displayOrDash(pickStr(row, 'caseNumber', 'case_number')),
    workflowStage: displayOrDash(workflowStage),
    status: statusRaw ? normalizeEscalationStatus(statusRaw) : 'Pending Assignment',
    taskStatus: statusRaw || undefined,
    escalatedAt: escalatedAtLabel ? displayOrDash(escalatedAtLabel) : undefined,
    overdueTime: overdueTime ? displayOrDash(overdueTime) : '',
    urgency: urgencyRaw ? normalizeUrgency(urgencyRaw) : undefined,
    currentAssignee: currentAssignee ? displayOrDash(currentAssignee) : undefined,
    assignedOfficer: assignedOfficerName ? displayOrDash(assignedOfficerName) : undefined,
    supervisor: assignedSupervisorName ? displayOrDash(assignedSupervisorName) : undefined,
    connectedAgdFileRefAvailable: pickConnectedAgdFileRefAvailable(row),
  };
}

function escalationColumnValue(row: EscalationTaskRow, col: string): string {
  switch (col) {
    case 'fileCategory':
      return row.fileCategory;
    case 'subFileCategory':
      return row.subFileCategory;
    case 'agency':
      return row.isAgdSubmitted ? 'AGD' : row.agency;
    case 'caseNumber':
      return row.caseNumber;
    case 'workflowStage':
      return row.workflowStage;
    case 'status':
      return row.status;
    case 'escalatedAt':
      return row.escalatedAt ?? '';
    case 'overdueTime':
      return row.overdueTime;
    case 'urgency':
      return row.urgency ?? '';
    case 'currentAssignee':
      return row.currentAssignee ?? '';
    case 'assignedOfficer':
      return row.assignedOfficer ?? '';
    case 'supervisor':
      return row.supervisor ?? '';
    default:
      return row.ref;
  }
}

function unwrapEscalationPage(data: unknown): ApiPage {
  if (!data || typeof data !== 'object') return {};
  const d = data as Record<string, unknown>;
  if (Array.isArray(d.content)) return d as ApiPage;

  const inner = d.data;
  if (inner && typeof inner === 'object') {
    const nested = inner as Record<string, unknown>;
    if (Array.isArray(nested.content)) return nested as ApiPage;
  }

  return d as ApiPage;
}

async function postEscalationTaskCaseRequestsPage(
  path: string,
  params: TaskParams,
  body: Record<string, unknown>,
): Promise<{ items: EscalationTaskRow[]; total: number; totalPages: number }> {
  const res = await caseApiRequest<ApiPage>(
    `${path}?pageNumber=${params.page ?? 0}&pageSize=${params.pageSize ?? 10}`,
    {
      method: 'POST',
      body,
      signal: params.signal,
    },
  );

  if (!res.ok) return { items: [] as EscalationTaskRow[], total: 0, totalPages: 1 };

  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const paged = adaptTaskAssignmentPage(unwrapEscalationPage(res.data), page, pageSize);

  return {
    items: paged.items.map((r) => mapEscalationApiRow(r as ApiRow)),
    total: paged.total,
    totalPages: paged.totalPages,
  };
}

/**
 * CMS — escalation unassigned queue.
 * POST `/v1/case-requests/escalation-tasks/unassign-task-case-requests`
 */
export async function listUnassignedEscalationTasks(params: TaskParams = {}) {
  return postEscalationTaskCaseRequestsPage(
    '/escalation-tasks/unassign-task-case-requests',
    params,
    buildEscalationUnassignedListBody(params),
  );
}

/**
 * CMS — escalation assigned queue.
 * POST `/v1/case-requests/escalation-tasks/assign-task-case-requests`
 */
export async function listAssignedEscalationTasks(params: TaskParams = {}) {
  return postEscalationTaskCaseRequestsPage(
    '/escalation-tasks/assign-task-case-requests',
    params,
    buildEscalationAssignedListBody(params),
  );
}

export async function fetchEscalationUnassignedColumnSuggestions(
  columnKey: string,
  prefix: string,
  signal?: AbortSignal,
): Promise<string[]> {
  const col = ESCALATION_UNASSIGNED_FILTER_COL_MAP[columnKey] ? columnKey : 'ref';
  const columnFilters = prefix.trim() ? { [col]: prefix.trim() } : {};
  const result = await listUnassignedEscalationTasks({ page: 0, pageSize: 200, columnFilters, signal });
  return uniqueColumnSuggestions(result.items.map((r) => escalationColumnValue(r, col)), prefix);
}

export async function fetchEscalationAssignedColumnSuggestions(
  columnKey: string,
  prefix: string,
  signal?: AbortSignal,
): Promise<string[]> {
  const col = ESCALATION_ASSIGNED_FILTER_COL_MAP[columnKey] ? columnKey : 'ref';
  const columnFilters = prefix.trim() ? { [col]: prefix.trim() } : {};
  const result = await listAssignedEscalationTasks({ page: 0, pageSize: 200, columnFilters, signal });
  return uniqueColumnSuggestions(result.items.map((r) => escalationColumnValue(r, col)), prefix);
}

/** POST `/v1/case-requests/escalation-tasks/assign-user-task` — escalation configure assignment save. */
export async function assignEscalationUserTask(input: {
  workflowInstanceStageUserTaskId: string;
  assignUserId: string;
  assignSupervisorId?: string;
  assignStateAttorneyId?: string;
  physicalFileMovementRequired?: boolean;
  documentMoverId?: string;
}): Promise<ApiResult<void>> {
  const workflowInstanceStageUserTaskId = input.workflowInstanceStageUserTaskId.trim();
  const assignUserId = input.assignUserId.trim();
  const assignSupervisorId = (input.assignSupervisorId ?? '').trim();
  const assignStateAttorneyId = (input.assignStateAttorneyId ?? '').trim();
  const physicalFileMovementRequired = input.physicalFileMovementRequired ?? false;
  const documentMoverId = (input.documentMoverId ?? '').trim();

  if (!workflowInstanceStageUserTaskId || !assignUserId) {
    return { success: false, message: 'Assignment details are incomplete.' };
  }
  if (physicalFileMovementRequired && !documentMoverId) {
    return { success: false, message: 'Document mover is required.' };
  }

  const body: Record<string, unknown> = {
    workflow_instance_stage_user_task_id: workflowInstanceStageUserTaskId,
    assign_user_id: assignUserId,
    document_mover_id: physicalFileMovementRequired ? documentMoverId : null,
    physical_file_movement_required: physicalFileMovementRequired,
  };
  if (assignSupervisorId) body.assign_supervisor_id = assignSupervisorId;
  if (assignStateAttorneyId) body.assign_state_attorney_id = assignStateAttorneyId;

  const res = await caseApiRequest<unknown>('/escalation-tasks/assign-user-task', {
    method: 'POST',
    body,
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  return { success: true, data: undefined };
}
